import { EDITOR_CONTENT_WIDTH_CSS_VAR } from '@eastcoal/editor/hooks/useTraceEditorContentSize/constants'

import { createEditorStyles } from './styleManager'

/* ************************************************************************** */
/*                                     注意                                    */
/* *********** 1. pdfhtml 对 css3 的支持不完全，尽量使用传统的 css 属性 *********** */
/* *********** 比如 paddingBlock、marginBlock 改回 padding、margin ************* */
/*                                                                             */
/* *************************************************************************** */

export const useThemeStyles = createEditorStyles(
  ({ token }) => ({
    textBase: {},
    textBold: {
      fontWeight: token.fontWeightStrong
    },
    textItalic: {
      fontStyle: 'italic'
    },
    textUnderline: {
      textDecoration: 'underline'
    },
    textStrikethrough: {
      textDecoration: 'line-through'
    },
    textUnderlineStrikethrough: {
      textDecoration: 'underline line-through'
    },
    textSubscript: {
      verticalAlign: 'sub',
      fontSize: '0.8em'
    },
    textSuperscript: {
      verticalAlign: 'super',
      fontSize: '0.8em'
    },
    paragraph: {
      position: 'relative',
      margin: 0
    },
    h1: {
      marginBottom: '0.5em',
      fontSize: token.fontSizeHeading2,
      fontWeight: token.fontWeightStrong
    },
    h2: {
      marginBottom: '0.5em',
      fontSize: token.fontSizeHeading3,
      fontWeight: token.fontWeightStrong
    },
    h3: {
      marginBottom: '0.5em',
      fontSize: token.fontSizeHeading4,
      fontWeight: token.fontWeightStrong
    },
    h4: {
      marginBottom: '0.5em',
      fontSize: token.fontSizeHeading5,
      fontWeight: token.fontWeightStrong
    },
    ol1: {
      margin: 0,
      padding: 0,
      listStyleType: 'decimal',
      listStylePosition: 'inside'
    },
    ol2: {
      margin: 0,
      padding: 0,
      listStyleType: 'lower-alpha',
      listStylePosition: 'inside'
    },
    ol3: {
      margin: 0,
      padding: 0,
      listStyleType: 'lower-roman',
      listStylePosition: 'inside'
    },
    ul: {
      margin: 0,
      padding: 0,
      listStylePosition: 'inside'
    },
    listItem: {
      margin: '0 1em'
    },
    listItemNested: {
      listStyleType: 'none',
      ['&:before,&:after']: {
        display: 'none'
      }
    },
    image: {
      display: 'inline-block',
      maxWidth: '100%',
      verticalAlign: 'bottom',
      fontSize: 0,
      lineHeight: 0
    },
    tableWrapper: {
      ['--lexical-table-border-color']: token.colorBorder,
      ['--lexical-table-selected-bg-color']: token.colorPrimaryBg,
      ['--lexical-table-marked-bg-color']: token.colorErrorBg,
      position: 'relative',
      paddingTop: token.paddingMD,
      paddingLeft: token.paddingMD,
      maxWidth: `var(${EDITOR_CONTENT_WIDTH_CSS_VAR})`,
      overflowX: 'auto'
    },
    /**
     * @deprecated 弃用内置的，让 TableWrapper 处理滚动
     */
    tableScrollableWrapper: {
      maxWidth: `var(${EDITOR_CONTENT_WIDTH_CSS_VAR})`,
      overflowX: 'auto'
    },
    tableController: {
      position: 'relative'
    },
    table: {
      width: 'fit-content',
      tableLayout: 'fixed',
      borderSpacing: 0,
      borderCollapse: 'collapse'
    },
    tableSelected: {
      outline: `1px solid ${token.colorPrimaryActive}`
    },
    tableCell: {
      position: 'relative',
      padding: token.paddingXXS,
      verticalAlign: 'top',
      textAlign: 'start',
      fontWeight: 'normal',
      outline: 'none',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'var(--lexical-table-border-color)'
    },
    tableCellHeader: {
      textAlign: 'start'
    },
    tableCellSelected: {
      opacity: 0.7,
      backgroundColor: 'var(--lexical-table-selected-bg-color)'
    },
    // marked 样式需要放在 selected 样式之后，否则会被 selected 样式覆盖
    tableCellMarked: {
      opacity: 0.7,
      backgroundColor: 'var(--lexical-table-marked-bg-color)'
    },
    textVariable: {},
    /* ************************************************************************** */
    /*      css 中只定义默认样式，用户配置的样式在 node#exportDom 中覆盖                */
    /* ************************************************************************** */
    tableVariable: {
      table: {
        width: '100%',
        borderCollapse: 'collapse',
        border: `1px solid ${token.colorBorder}`,
        /* ********************************** 单元格边距 ********************************* */
        '& th, & td': {
          margin: 0,
          padding: token.paddingXXS
        },
        /* *********************************** 边框 *********************************** */
        '&.border-row th, &.border-row td': {
          borderBottom: `1px solid ${token.colorBorder}`
        },
        '&.border-cell th, &.border-cell td': {
          borderBottom: `1px solid ${token.colorBorder}`,
          borderRight: `1px solid ${token.colorBorder}`
        },
        '&>tbody>tr:last-child>td': {
          borderBottom: 'none'
        },
        '& th:last-child, & td:last-child': {
          borderRight: 'none'
        },
        /* ********************************** 水平对齐 ********************************** */
        '&.align-left th, &.align-left td': {
          textAlign: 'left'
        },
        '&.align-center th, &.align-center td': {
          textAlign: 'center'
        },
        '&.align-right th, &.align-right td': {
          textAlign: 'right'
        },
        /* ********************************** 垂直对齐 ********************************** */
        '&.vertical-top th, &.vertical-top td': {
          verticalAlign: 'top'
        },
        '&.vertical-middle th, &.vertical-middle td': {
          verticalAlign: 'middle'
        },
        '&.vertical-bottom th, &.vertical-bottom td': {
          verticalAlign: 'bottom'
        },
        /* ********************************** 表头加粗 ********************************* */
        '& thead': {
          fontWeight: 'normal'
        }
      }
    }
  }),
  {
    label: 'editor-theme'
  }
)
